"use client";

import { useState, useMemo } from "react";
import Link from "next/link";
import { Search, X } from "lucide-react";

interface Term {
  slug: string;
  term: string;
  definition: string;
}

interface GlossarySearchProps {
  terms: Term[];
}

export function GlossarySearch({ terms }: GlossarySearchProps) {
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    if (!query.trim()) return [];
    const lower = query.toLowerCase();
    return terms.filter(
      (t) =>
        t.term.toLowerCase().includes(lower) ||
        t.definition.toLowerCase().includes(lower),
    );
  }, [query, terms]);

  const showResults = query.trim().length > 0;

  return (
    <div className="relative">
      {/* Search input */}
      <div className="relative">
        <Search
          className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-secondary"
          aria-hidden="true"
        />
        <input
          type="search"
          placeholder="Search AI terms…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Search glossary terms"
          className="min-h-[44px] w-full rounded-md border border-line bg-paper py-2.5 pl-10 pr-10 text-base text-dark placeholder:text-mid/60 transition-colors hover:border-mid/50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-secondary"
        />
        {query && (
          <button
            type="button"
            onClick={() => setQuery("")}
            aria-label="Clear search"
            className="absolute right-3 top-1/2 -translate-y-1/2 rounded-sm p-0.5 text-mid transition-colors hover:text-secondary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-secondary"
          >
            <X className="size-4" aria-hidden="true" />
          </button>
        )}
      </div>

      {/* Results dropdown */}
      {showResults && (
        <div className="absolute left-0 right-0 top-full z-20 mt-2 max-h-80 overflow-y-auto rounded-md border border-line bg-paper shadow-sm">
          {filtered.length === 0 ? (
            <div className="px-4 py-6 text-center text-sm text-mid">
              No terms match &ldquo;{query}&rdquo;
            </div>
          ) : (
            <ul className="divide-y divide-line">
              {filtered.slice(0, 15).map((term) => (
                <li key={term.slug}>
                  <Link
                    href={`/glossary/${term.slug}`}
                    className="block px-4 py-3 transition-colors hover:bg-light-bg"
                    onClick={() => setQuery("")}
                  >
                    <span className="font-heading font-semibold text-dark">
                      {term.term}
                    </span>
                    <span className="mt-0.5 block text-sm text-mid line-clamp-1">
                      {term.definition}
                    </span>
                  </Link>
                </li>
              ))}
              {filtered.length > 15 && (
                <li className="px-4 py-3 text-center text-xs text-mid">
                  Showing 15 of {filtered.length} results. Refine your search
                  for more specific results.
                </li>
              )}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
